import React, { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { yooKassaModalClose, setPendingPrivatePayment, createPaymentYookassa } from '../../../stores/payment.store';
import { MainContext } from '../../../context/main.context';
import {
    PAYMENTS_YOOKASSA_CLOSE_WIDGET_ERROR,
    PAYMENTS_YOOKASSA_CREATE_ERROR,
    PAYMENTS_YOOKASSA_FAIL_ERROR,
    PAYMENTS_YOOKASSA_INITIALIZING_WIDGET_ERROR,
    PAYMENTS_YOOKASSA_SET_PENDING_PRIVATE_ERROR,
} from '../../../common/errorCodes';
import ManualController from '../../../hoc/ManualController';
import Payments from '../index';


export default function PaymentsYookassa() {
    const navigate = useNavigate();
    const { manualRedirect } = ManualController();
    const { modalContent, paymentId, locale, project, source, jwt, toggleLoader, setModalContent } = useContext(MainContext);

    useEffect(() => {
        const script = document.createElement('script');
        script.src = process.env.REACT_APP_YOOKASSA_SRC as string;
        script.async = true;
        script.onload = _createPayment;

        document.body.appendChild(script);

        return () => {
            window.YooMoneyCheckoutWidget = null
            document.body.removeChild(script);
        };
    }, []);

    const _initializingWidget = (confirmationToken: string): void => {
        const checkout = new window.YooMoneyCheckoutWidget({
            confirmation_token: confirmationToken,
            customization: {
                modal: true,
                colors: {
                    background: '#F2F3F5',
                    control_primary: '#2368a2',
                    border: '#D4D4D4',
                    text: '#222222'
                }
            },
            error_callback: (err: any): void => {
                const errData = err?.response?.data || err;
                manualRedirect(errData, 'payment', true, PAYMENTS_YOOKASSA_INITIALIZING_WIDGET_ERROR);
            },
        });

        checkout.render('payment-form');

        checkout.on('modal_close', (): void => _yooKassaModalClose());

        checkout.on('success', (): void => {
            checkout.destroy();

            toggleLoader(true);

            setPendingPrivatePayment(paymentId).then(res => {
                const result: boolean = res?.data.result;
                const payload: string = res?.data.payload;

                if (result) {
                    manualRedirect({ payload }, 'payment');
                    return;
                }

                manualRedirect(res, 'payment', true, PAYMENTS_YOOKASSA_SET_PENDING_PRIVATE_ERROR);
            }, err => {
                const errData = err?.response?.data || err;
                manualRedirect(errData, 'payment', true, PAYMENTS_YOOKASSA_SET_PENDING_PRIVATE_ERROR);
            }).finally(() => {
                toggleLoader(false);
            });
        });

        checkout.on('fail', (): void => {
            checkout.destroy();
            manualRedirect({}, 'payment', true, PAYMENTS_YOOKASSA_FAIL_ERROR);
        });
    }

    const _createPayment = (): void => {
        toggleLoader(true);

        createPaymentYookassa(paymentId).then(res => {
            const confirmationToken = res?.data?.confirmationToken;

            if (confirmationToken) {
                _initializingWidget(confirmationToken);
                return;
            }

            manualRedirect(res, 'payment', true, PAYMENTS_YOOKASSA_CREATE_ERROR);
        }, err => {
            const errData = err?.response?.data || err;
            manualRedirect(errData, 'payment', true, PAYMENTS_YOOKASSA_CREATE_ERROR);
        }).finally((): void => {
            toggleLoader(false);
        });
    }

    const _navigate = (path: string): void => {
        navigate({
            pathname: path,
            search: `?id=${paymentId}&source=${source}&locale=${locale}&project=${project}&jwt=${jwt}`,
        });
    }

    const _yooKassaModalClose = (): void => {
        toggleLoader(true);

        yooKassaModalClose(paymentId).then(res => {
            const result = res?.data?.result;

            if (result) {
                if (modalContent) {
                    setModalContent({ component: <Payments/> });
                    return;
                }

                _navigate('/payments/process');
                return;
            }

            manualRedirect(res, 'payment', true, PAYMENTS_YOOKASSA_CLOSE_WIDGET_ERROR);
        }, err => {
            const errData = err?.response?.data || err;
            manualRedirect(errData, 'payment', true, PAYMENTS_YOOKASSA_CLOSE_WIDGET_ERROR);
        }).finally((): void => {
            toggleLoader(false);
        });
    }

    return (
        <div className="yookassa"></div>
    );
}

PaymentsYookassa.displayName = 'PaymentsYookassa';